import type { ReactNode } from "react";
import { APILoadingStatus, APIProvider, useApiLoadingStatus } from "@vis.gl/react-google-maps";
import { MapPinOff } from "lucide-react";

import { GOOGLE_MAPS_API_KEY, hasGoogleMapsKey } from "@/lib/google-maps";

function Unavailable({ className, title, detail }: { className?: string; title: string; detail: string }) {
  return (
    <div
      className={`flex flex-col items-center justify-center gap-1.5 rounded-lg border border-dashed bg-muted/30 p-4 text-center ${className ?? ""}`}
    >
      <MapPinOff className="h-5 w-5 text-muted-foreground" />
      <p className="text-sm font-medium">{title}</p>
      <p className="max-w-sm text-xs text-muted-foreground">{detail}</p>
    </div>
  );
}

/**
 * Surfaces Google's load outcome instead of leaving a grey box.
 *
 * AUTH_FAILURE is worth its own message: it is what a key whose referrer list
 * does not include this domain produces, and it looks identical to "the map is
 * broken" unless something says otherwise. The QA report that prompted this
 * change was precisely a map failing with no explanation.
 */
function LoadGuard({ children, className }: { children: ReactNode; className?: string }) {
  const status = useApiLoadingStatus();
  if (status === APILoadingStatus.AUTH_FAILURE) {
    return (
      <Unavailable
        className={className}
        title="Google Maps rejected this site"
        detail={`The map key does not permit ${window.location.host}. Add it to the key's HTTP referrer restrictions in Google Cloud.`}
      />
    );
  }
  if (status === APILoadingStatus.FAILED) {
    return (
      <Unavailable
        className={className}
        title="The map could not load"
        detail="Check your connection. The coordinates below can still be entered by hand."
      />
    );
  }
  return <>{children}</>;
}

/**
 * Wrap anything that renders a Google map. Without a key it renders a clear
 * placeholder rather than a broken map, so a form that happens to contain a map
 * stays usable — coordinates can still be typed.
 *
 * Each map mounts its own provider, so the Maps script loads only on pages that
 * actually show a map. Identical options mean the loader fetches it once.
 */
export function GoogleMapsGate({ children, className }: { children: ReactNode; className?: string }) {
  if (!hasGoogleMapsKey) {
    return (
      <Unavailable
        className={className}
        title="Map not configured"
        detail="Set VITE_GOOGLE_MAPS_API_KEY to show the map. Coordinates can still be entered below."
      />
    );
  }
  return (
    <APIProvider apiKey={GOOGLE_MAPS_API_KEY} libraries={["places"]}>
      <LoadGuard className={className}>{children}</LoadGuard>
    </APIProvider>
  );
}
